import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';
import { getImageUrl } from '../config/imageUrl';
import { dishService } from '../services/dish.service';
import { Dish } from '../types/dish.types';

export const DishEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { success, error } = useNotification();

  const [dish, setDish] = useState<Dish | null>(null);
  const [loading, setLoading] = useState(true);
  const [dishName, setDishName] = useState('');
  const [newWeight, setNewWeight] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);

  // Загрузка данных блюда
  useEffect(() => {
    const fetchDish = async () => {
      try {
        const data = await dishService.getDishById(Number(id));
        setDish(data);
        setDishName(data.name);
        if (data.image) {
          setImagePreview(getImageUrl(data.image));
        }
      } catch (err) {
        error(t('dishEdit.error'), t('dishEdit.errorLoadFailed'));
        navigate('/menu');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDish();
    }
  }, [id, navigate, error, t]);

  // Обработка изменения изображения
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Сохранение изменений блюда
  const handleSaveDish = async () => {
    if (!dishName.trim()) {
      error(t('dishEdit.errorValidation'), t('dishEdit.errorFillAllFields'));
      return;
    }

    try {
      const updatedDish = await dishService.updateDish(Number(id), {
        name: dishName,
        image: imageFile || undefined,
      });

      setDish(updatedDish);
      setImageFile(null);
      success(t('dishEdit.success'), t('dishEdit.successUpdated'));
    } catch (err) {
      error(t('dishEdit.error'), t('dishEdit.errorUpdateFailed'));
    }
  };

  // Добавление граммовки
  const handleAddPortion = async () => {
    const weight = parseInt(newWeight, 10);

    // Проверяем, что weight является валидным числом и больше 0
    if (isNaN(weight) || weight <= 0) {
      error(t('dishEdit.errorValidation'), t('dishEdit.errorInvalidWeight'));
      return;
    }

    // Проверяем, не существует ли уже такая граммовка
    const existingPortion = dish?.portions.find(
      (p) => !p.is_deleted && p.weight === weight
    );

    if (existingPortion) {
      error(t('dishEdit.errorValidation'), t('dishEdit.errorDuplicateWeight'));
      return;
    }

    try {
      const newPortion = await dishService.createPortion({
        dish_id: Number(id),
        weight: weight,
        default_price: 0,
      });

      setDish((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          portions: [...prev.portions, newPortion],
        };
      });
      setNewWeight('');
      success(t('dishEdit.success'), t('dishEdit.successPortionAdded'));
    } catch (err) {
      console.error('Ошибка добавления граммовки:', err);
      error(t('dishEdit.error'), t('dishEdit.errorPortionAddFailed'));
    }
  };

  // Удаление граммовки
  const handleDeletePortion = async (portionId: number) => {
    try {
      await dishService.deletePortion(portionId);

      setDish((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          portions: prev.portions.filter((p) => p.id !== portionId),
        };
      });
      success(t('dishEdit.success'), t('dishEdit.successPortionDeleted'));
    } catch (err) {
      error(t('dishEdit.error'), t('dishEdit.errorPortionDeleteFailed'));
    }
  };

  if (loading) {
    return (
      <div className="container-app py-8">
        <div className="text-center">
          <p className="text-text-secondary">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!dish) {
    return null;
  }

  return (
    <div className="container-app py-8">
      {/* Заголовок */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">
            {t('dishEdit.title')}
          </h1>
          <p className="text-text-secondary">{t('dishEdit.subtitle')}</p>
        </div>
        <button
          onClick={() => navigate('/menu')}
          className="btn btn-secondary"
        >
          {t('dishEdit.backToMenu')}
        </button>
      </div>

      {/* Основная информация */}
      <div className="card mb-6">
        <h2 className="text-xl font-semibold text-text-primary mb-4">
          {t('dishEdit.sectionBasicInfo')}
        </h2>

        {/* Изображение */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-text-secondary mb-2">
            {t('dishEdit.fieldImage')}
          </label>
          <div className="flex flex-col sm:flex-row items-start gap-4">
            {imagePreview && (
              <div className="w-32 h-32 bg-background-200 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src={imagePreview}
                  alt={dishName}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="flex-1">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="block w-full text-sm text-text-secondary file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 cursor-pointer"
              />
              <p className="mt-2 text-xs text-text-tertiary">
                {t('dishEdit.imageHint')}
              </p>
            </div>
          </div>
        </div>

        {/* Название */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-text-secondary mb-2">
            {t('dishEdit.fieldName')}
          </label>
          <input
            type="text"
            value={dishName}
            onChange={(e) => setDishName(e.target.value)}
            className="input-field"
            placeholder={t('dishEdit.placeholderName')}
          />
        </div>

        {/* Кнопка сохранения */}
        <div className="flex justify-end">
          <button onClick={handleSaveDish} className="btn btn-primary">
            {t('dishEdit.saveChanges')}
          </button>
        </div>
      </div>

      {/* Граммовки */}
      <div className="card">
        <h2 className="text-xl font-semibold text-text-primary mb-4">
          {t('dishEdit.sectionPortions')}
        </h2>

        {/* Добавление граммовки */}
        <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
          <label className="block text-sm font-medium text-text-primary mb-3">
            {t('dishEdit.addPortion')}
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <input
                type="number"
                value={newWeight}
                onChange={(e) => setNewWeight(e.target.value)}
                className="input-field w-full"
                placeholder={t('dishEdit.placeholderWeight')}
                min="1"
                step="1"
              />
              <p className="text-xs text-text-tertiary mt-1">
                {t('dishEdit.weightHint')}
              </p>
            </div>
            <button
              onClick={handleAddPortion}
              className="btn btn-primary whitespace-nowrap self-start"
            >
              + {t('dishEdit.addPortion')}
            </button>
          </div>
        </div>

        {/* Список граммовок */}
        <div>
          <h3 className="text-sm font-medium text-text-secondary mb-3">
            {t('dishEdit.existingPortions')}
          </h3>
          {dish.portions.filter((p) => !p.is_deleted).length === 0 ? (
            <div className="text-center py-8 bg-background-100 rounded-lg border-2 border-dashed border-background-300">
              <svg
                className="w-12 h-12 mx-auto mb-3 text-text-tertiary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              <p className="text-text-tertiary">{t('dishEdit.noPortions')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {dish.portions
                .filter((p) => !p.is_deleted)
                .sort((a, b) => a.weight - b.weight)
                .map((portion) => (
                  <div
                    key={portion.id}
                    className="flex items-center justify-between p-4 bg-white border border-background-300 rounded-lg hover:border-primary-300 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-primary-600">
                        {portion.weight}
                      </span>
                      <span className="text-sm text-text-secondary">
                        {t('dishEdit.grams')}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDeletePortion(portion.id)}
                      className="btn btn-error btn-sm"
                      title={t('dishEdit.deletePortion')}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
